import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginRequest } from '../types';
import { APP_VERSION } from '../config/brand';
import { Icon, Spinner } from '../components/ui';

const Login: React.FC = () => {
  const { login, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [credentials, setCredentials] = useState<LoginRequest>({
    username: '',
    password: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner className="h-12 w-12 text-brand-600" />
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await login(credentials);
    } catch (err) {
      setError(err instanceof Error && err.message && err.message !== 'Login failed' ? err.message : 'Benutzername oder Passwort stimmen nicht.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          {/* Der Tropfen fällt ein und landet: zwei Wellenringe laufen an seinem Fuß aus (60 % der Bildhöhe). */}
          <div className="relative mx-auto" style={{ width: 120, height: 120 }}>
            {[0.5, 0.75].map(verzoegerung => (
              <span key={verzoegerung} aria-hidden
                className="ui-welle absolute rounded-[50%] border-2 border-brand-300"
                style={{ left: '50%', top: '60%', width: 96, height: 14, animationDelay: `${verzoegerung}s` }} />
            ))}
            <img src="/hp-logo.png" alt="Heinze Pool" className="ui-einfallen relative" style={{ width: 120, height: 120 }} />
          </div>
          <h2 className="mt-4 text-center text-3xl font-extrabold text-gray-900">
            Heinze Portal
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Melden Sie sich mit Ihren Zugangsdaten an
          </p>
        </div>

        <form className="ui-pop mt-8 space-y-6" style={{ animationDelay: '0.35s', animationFillMode: 'both' }} onSubmit={handleSubmit}>
          <input type="hidden" name="remember" value="true" />
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="username" className="sr-only">
                Benutzername
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-brand-500 focus:border-brand-500 focus:z-10 sm:text-sm"
                placeholder="Benutzername"
                value={credentials.username}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Passwort
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-brand-500 focus:border-brand-500 focus:z-10 sm:text-sm"
                placeholder="Passwort"
                value={credentials.password}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Icon.Warnung className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Anmeldung fehlgeschlagen
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <Spinner className="-ml-1 mr-3 h-5 w-5 text-white" />
              ) : (
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  <Icon.Schluessel className="h-5 w-5 text-brand-500 group-hover:text-brand-400" />
                </span>
              )}
              {isSubmitting ? 'Anmelden …' : 'Anmelden'}
            </button>
          </div>

        </form>

        <p className="mt-6 text-center text-xs text-gray-400">v{APP_VERSION}</p>
      </div>
    </div>
  );
};

export default Login;
